class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  // ----- Start customization for calling three API requests: standard products, treatment products, and search suggestions -----

  onFocus(event) {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      const treatmentUrl = this.setupTreatmentURL().toString();
      const productUrl = this.setupProductURL().toString();
      const suggestionUrl = this.setupSuggestionURL().toString();
      this.renderDualSection(treatmentUrl, productUrl, suggestionUrl);
    }
  }
  getQuery() {
    return this.input.value.trim();
  }
  clear(event = null) {
    event && event.preventDefault();
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
  }

  // Setup URL for treatment search
  setupTreatmentURL() {
    const url = new URL(`${FoxTheme.routes.shop_url}${FoxTheme.routes.predictive_search_url}`);
    const query = `${this.getQuery()} product_type:Treatment`;
    url.searchParams.set('q', query);
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,body,variants.sku');
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  // Setup URL for standard products
  setupProductURL() {
    const url = new URL(`${FoxTheme.routes.shop_url}${FoxTheme.routes.predictive_search_url}`);
    const query = `${this.getQuery()} -product_type:Treatment`;
    url.searchParams.set('q', query);
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,body,variants.sku');
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  // Setup URL for search suggestions
  setupSuggestionURL() {
    const url = new URL(`${FoxTheme.routes.shop_url}/search/suggest.json`);
    url.searchParams.set('q', this.getQuery());
    url.searchParams.set('resources[type]', 'query');
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 4);
    return url;
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const treatmentUrl = this.setupTreatmentURL().toString();
    const productUrl = this.setupProductURL().toString();
    const suggestionUrl = this.setupSuggestionURL().toString();
    this.renderDualSection(treatmentUrl, productUrl, suggestionUrl);
  }

  // Handle dual search with proper caching
  renderDualSection(treatmentUrl, productUrl, suggestionUrl) {
    const cacheKey = `${treatmentUrl}|${productUrl}|${suggestionUrl}`;

    if (this.cachedMap.has(cacheKey)) {
      this.renderSectionFromCache(cacheKey);
    } else {
      this.renderSectionFromFetch(treatmentUrl, productUrl, suggestionUrl, cacheKey);
    }
  }

  renderSectionFromCache(cacheKey) {
    const responseText = this.cachedMap.get(cacheKey);
    this.renderSearchResults(responseText);
    this.setAttribute('results', 'true');
  }

  renderSectionFromFetch(treatmentUrl, productUrl, suggestionUrl, cacheKey) {
    // Indicate loading state
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');

    // Fetch both searches concurrently
    const treatmentFetch = fetch(treatmentUrl).then(r => {
      if (!r.ok) throw new Error(`Treatment search failed: ${r.status}`);
      return r.text();
    });

    const productFetch = fetch(productUrl).then(r => {
      if (!r.ok) throw new Error(`Product search failed: ${r.status}`);
      return r.text();
    });

    const suggestionFetch = fetch(suggestionUrl).then(r => {
      if (!r.ok) throw new Error(`Suggestion search failed: ${r.status}`);
      return r.json();
    });

    // Wait for all three to complete
    Promise.all([treatmentFetch, productFetch, suggestionFetch])
      .then(([treatmentData, productData, suggestionData]) => {
        // Combine the results
        const combinedHTML = this.combineSearchResults(treatmentData, productData, suggestionData);

        // Store in cache before rendering
        this.cachedMap.set(cacheKey, combinedHTML);

        // Render the combined results
        this.renderSearchResults(combinedHTML);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        // Clean up loading state
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
        this.setAttribute('results', 'true');
      });
  }

  // Combine search results from both APIs
  combineSearchResults(treatmentData, productData, suggestionData) {
    const parser = new DOMParser();
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);

    const treatmentDoc = parser.parseFromString(treatmentData, 'text/html');
    const productDoc = parser.parseFromString(productData, 'text/html');

    const treatmentContent = treatmentDoc.getElementById(id);
    const productContent = productDoc.getElementById(id);

    // Return empty state if no products
    if (!treatmentContent || !productContent) {
      return this.createEmptyState();
    }

    // Extract elements
    const elements = {
      treatmentProducts: Array.from(treatmentContent.querySelectorAll('.predictive-search__list-item') || []),
      standardProducts: Array.from(productContent.querySelectorAll('.predictive-search__list-item') || []),
      suggestions: productContent.querySelector('.predictive-search-result--queries') ||
        treatmentContent.querySelector('.predictive-search-result--queries'),
      spaFinder: productContent.querySelector('.predictive-search-result--spas') ||
        treatmentContent.querySelector('.predictive-search-result--spas'),
      showAll: productContent.querySelector('.search__results-all') ||
        treatmentContent.querySelector('.search__results-all')
    };

    const hasProducts = elements.treatmentProducts.length > 0 || elements.standardProducts.length > 0;

    // Return empty state if no products
    if (!hasProducts) {
      const emptyState = productContent.querySelector('.empty-state') ||
        treatmentContent.querySelector('.empty-state');
      return emptyState?.outerHTML || this.createEmptyState();
    }

    // Build product sections
    const sections = [];

    // Add suggestions from API if available
    const suggestionsHTML = this.buildSuggestionsFromAPI(suggestionData);
    if (suggestionsHTML) {
      sections.push(suggestionsHTML);
    } else if (elements.suggestions) {
      sections.push(elements.suggestions.outerHTML);
    }

    if (elements.standardProducts.length > 0) {
      sections.push(this.createProductSection(
        'Standard Products',
        elements.standardProducts,
        productContent.querySelector('.standard-product-label')?.textContent,
        elements.treatmentProducts.length === 0
      ));
    }

    if (elements.treatmentProducts.length > 0) {
      sections.push(this.createProductSection(
        'Treatment Products',
        elements.treatmentProducts,
        treatmentContent.querySelector('.treatment-product-label')?.textContent,
        elements.standardProducts.length === 0
      ));
    }

    if (elements.spaFinder) {
      sections.push(elements.spaFinder.outerHTML);
    }

    if (elements.showAll) {
      sections.push(elements.showAll.outerHTML);
    }

    return `
    <div class="flex flex-col lg:flex-row gap-10 w-full">
      <div class="flex flex-col lg:flex-row flex-wrap gap-y-4 lg:gap-y-10 gap-x-10">
        ${sections.join('')}
      </div>
    </div>
  `;
  }

  // Build suggestions HTML from the suggestion API response
  buildSuggestionsFromAPI(suggestionData) {
    if (!suggestionData || !suggestionData.resources || !suggestionData.resources.results) {
      return null;
    }

    const queries = suggestionData.resources.results.queries || [];

    if (queries.length === 0) {
      return null;
    }

    const suggestionItems = queries.map((query, index) => {
      return `
        <li
          id="predictive-search-option-query-${index + 1}"
          class="predictive-search-result__list-item"
          role="option"
          aria-selected="false"
        >
          <a href="${query.url}" class="reversed-link" aria-label="${query.text}">
            ${query.styled_text || query.text}
          </a>
        </li>
      `;
    }).join('');

    return `
      <div class="predictive-search-result search__box-item predictive-search-result--queries">
        <h4 class="h5 predictive-search-result__heading">
          suggestions
        </h4>
        <ul
          id="predictive-search-results-queries-list"
          role="group"
          aria-labelledby="predictive-search-queries"
          class="predictive-search-result__list flex flex-col flex-wrap gap-x-5 gap-y-2"
        >
          ${suggestionItems}
        </ul>
      </div>
    `;
  }

  // Create product sections with proper structure
  createProductSection(defaultLabel, products, labelText, useDoubleColumn) {
    const label = labelText || defaultLabel;
    const gridClass = useDoubleColumn ? 'lg:f-grid-2-cols' : '';
    const productHTML = products.map(p => p.outerHTML).join('');
    const sectionClass = defaultLabel.toLowerCase().replace(' ', '-');

    return `
      <div class="predictive-search-result--${sectionClass}">
        <h4 class="h5 predictive-search-result__heading">${label}</h4>
        <ul class="grid gap-4 ${gridClass}">${productHTML}</ul>
      </div>
    `;
  }

  // Helper for empty state
  createEmptyState() {
    const template = document.querySelector('.empty-state');
    return template ? template.innerHTML : '<p class="text-center">Your search returned no results.</p>';
  }

  // Render the combined search results
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      this.searchContent?.classList.remove('hidden');
      targetElement.innerHTML = responseText;
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }
  // ----- End Customization -----

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);
